import {
  AppBar,
  Container,
  Grid,
  Toolbar,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Hidden,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Slide,
  Snackbar,
  Alert,
} from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { makeStyles, withStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";

//... Date imports
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

//... Service  imports.
import { EmailNotificationService } from "../../services/notification.Service";

//... Icons imports.
import EmailIcon from "@mui/icons-material/Email";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Helper from "../../common/Helper";
import RouteConstants from "../../common/RouteConstants";
import Loader from "../../sharedComponents/loader/loader";
import TitleTooltip from "../../sharedComponents/Tooltip/TitleTooltip";

//... Context provider imports.
import AppDataContext from "../../common/appContext";

//... Shared components imports.
import Paging from "../../sharedComponents/Pagination/paging";

const useStyles = makeStyles((theme) => ({
  appbarBackground: {
    backgroundColor: "#fff",
    color: "#1d428a",
  },
  title: {
    flexGrow: 1,
  },
  btn: {
    padding: "15px",
    [theme.breakpoints.down("sm")]: {
      marginBottom: "10px",
    },
    marginLeft: "10px",
  },
  container: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  table: {
    [theme.breakpoints.up("md")]: {
      minWidth: 700,
    },
  },
  tableContainer: {
    padding: theme.spacing(2),
    width: "100%",
  },
  tablesortactivelabel: {
    color: "#fff !important",
  },
  tablesorticon: {
    color: "#ccc !important",
  },
  tablesortlabelroot: {
    "&:hover": { color: "#fff !important" },
    color: "#ADD8E6 !important",
  },
  PaginationWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },
  pagination: {
    "& .MuiPaginationItem-root": {
      fontSize: theme.spacing(1.5),
      minWidth: theme.spacing(2.5),
      height: theme.spacing(2.5),
    },
    "& .MuiSelect-select": {
      padding: theme.spacing(0.5, 1),
      fontSize: theme.spacing(1.5),
    },
  },
}));

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#24428a",
    color: theme.palette.common.white,
  },
  body: {
    padding: theme.spacing(0, 2),
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

//... Constant data mappings.
const headers = [
  { label: "Recipients", key: "dL_OptionsText" },
  { label: "Additional Email-Ids", key: "additional_To" },
  { label: "From Name", key: "fromName" },
  { label: "Subject Line", key: "subject" },
  { label: "Schedule Date", key: "scheduledDate" },
  { label: "Email Status", key: "emailStatus" },
];

const columnWidths = {
  Recipients: "10%%",
  "Additional Email-Ids": "20%",
  "From Name": "10%",
  "Subject Line": "20%",
  "Schedule Date": "15%",
  "Email Status": "5%",
  "": "10%",
};

const sortableHeaders = [
  "From Name",
  "Subject Line",
  "Schedule Date",
  "emailStatus",
];

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ManageEmailNotification = () => {
  const classes = useStyles();
  //... Route
  const navigate = useNavigate();

  //... Context
  const { user } = useContext(AppDataContext);

  //... Filter data holders.
  const [dates, setDates] = React.useState({
    fromDate: dayjs(),
    toDate: dayjs(),
  });
  const [emailStatus, setEmailStatus] = React.useState("");
  const [emailStatusList, setEmailStatusList] = useState([]);
  const [notificationList, setNotificationList] = useState([]);
  const [notificationListCopy, setNotificationListCopy] = useState([]);

  //... Pagination data holders.
  const [pagingInfo, setPagingInfo] = useState({
    currentPage: 1,
    rowsPerPage: 10,
    noOfPages: null,
  });
  const [currentPageList, setCurrentPageList] = useState([]);

  //... Sorting data holders.
  const [orderBy, setOrderBy] = useState(""); //SORT BY
  const [order, setOrder] = useState("asc"); //SORT ORDER

  //... Dialog data holders.
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  //... Loader data holders
  const [isLoading, setIsLoading] = useState(false);

  //... Alert data holders
  const [alert, setAlert] = useState(false);
  const [alertType, setAlertType] = useState("warning");
  const [alertMessage, setAlertMessage] = useState("");

  //... Ref constant data holders.
  const searchResultCount = useRef(0);
  const stateChangeRef = useRef();
  const selectedFileRef = useRef(null);

  useEffect(() => {
    getEnumerationData();

    //...Logic to get preserved states from localStorage if exist and update current states.
    const searchFilterJSON = localStorage.getItem("searchNotiFicationFilter");

    if (searchFilterJSON) {
      const states = searchFilterJSON ? JSON.parse(searchFilterJSON) : null;

      // Check if the states have changed
      if (!areStatesEqual(states, stateChangeRef.current)) {
        setPreservedStates(states);

        // Update the ref to the new state
        stateChangeRef.current = states;
      }
    }
  }, []);

  //.. This effect ensures the paging info updated based on data or list.
  useEffect(() => {
    if (notificationList.length > 0) {
      const noOfPages = Math.ceil(
        notificationList.length / pagingInfo.rowsPerPage
      );
      const newCurrentPage = Math.min(pagingInfo.currentPage, noOfPages);

      setPagingInfo({
        ...pagingInfo,
        currentPage: newCurrentPage,
        noOfPages: noOfPages,
      });

      getReviewList(newCurrentPage, pagingInfo.rowsPerPage);
    } else {
      setPagingInfo({
        ...pagingInfo,
        currentPage: 1,
        noOfPages: Math.ceil(notificationList.length / pagingInfo.rowsPerPage),
      });
      getReviewList(1, pagingInfo.rowsPerPage);
    }
  }, [notificationList]);

  //... This effect ensures if navigation request comes from edit or add and all states are set call APIs services to fetch result.
  useEffect(() => {
    const { current: currentState } = stateChangeRef;

    if (currentState) {
      const timeoutId = setTimeout(() => {
        getEmailNotificationList();
        localStorage.removeItem("searchNotiFicationFilter");
      }, 500);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    return () => {}; // Empty cleanup function if currentState is falsy
  }, [stateChangeRef.current]);

  //... Handlers
  const handleRowPerPageChange = (rowsPerPage) => {
    setPagingInfo({
      currentPage: 1,
      rowsPerPage: rowsPerPage,
      noOfPages: Math.ceil(notificationList.length / rowsPerPage),
    });
    getReviewList(1, rowsPerPage);
  };

  const onPageChange = (value) => {
    setPagingInfo({
      ...pagingInfo,
      currentPage: value,
    });
    getReviewList(value, pagingInfo.rowsPerPage);
  };

  const handleDateChange = (key, newValue) => {
    setDates((prevDates) => ({ ...prevDates, [key]: newValue }));
  };

  const handleEmailStatusChange = (event) => {
    setEmailStatus(event.target.value);
  };

  const handleSort = (orderByColumn) => {
    let newOrder =
      (orderBy === "" || orderBy === orderByColumn) && order === "asc"
        ? "desc"
        : "asc";
    let res = headers.filter((i) => i.label === orderByColumn);

    if (res && res.length > 0) {
      let orderByCol = res[0].key;
      let newRows = [...notificationListCopy];
      if (orderByCol === "scheduledDate" || orderByCol === "ModifiedDate") {
        if (orderByCol === "scheduledDate") {
          newRows.sort(function (a, b) {
            return new Date(a.scheduledDate) - new Date(b.scheduledDate);
          });
        } else if (orderByCol === "ModifiedDate") {
          newRows.sort(function (a, b) {
            return new Date(a.ModifiedDate) - new Date(b.ModifiedDate);
          });
        }

        if (newOrder === "desc") {
          newRows = newRows.reverse();
        }
        setOrder(newOrder);
        setOrderBy(orderByColumn);
        setNotificationList([...newRows]);
      } else {
        let result = Helper.stableSort(
          [...notificationList],
          Helper.getComparator(newOrder, orderByCol)
        );
        setOrder(newOrder);
        setOrderBy(orderByColumn);
        setNotificationList([...result]);
      }
    }
  };

  const handleNotificationAction = (notificationID, source = "add/update") => {
    localStorage.setItem(
      "searchNotiFicationFilter",
      JSON.stringify(getCurrentSearchFilters())
    );

    const route =
      source === "add/update"
        ? `${RouteConstants.EMAIL_NOTIFICATION}/${notificationID}`
        : `${RouteConstants.VIEW_EMAIL_NOTIFICATION}/${notificationID}`;

    navigate(route);
  };

  const handleDeleteMedia = (details) => {
    selectedFileRef.current = details;
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteDialogAgree = () => {
    setOpenDeleteDialog(false);
    deleteMediaFromDB();
  };

  async function getEnumerationData() {
    setIsLoading(true);
    const emptyStatusList = {
      childID: "",
      childName: "None",
    };
    try {
      const [EmailStatusList] = await Promise.all([
        EmailNotificationService.getEmailStatusList(),
      ]);
      setEmailStatusList([emptyStatusList, ...(EmailStatusList.data || [])]);
      setIsLoading(false);
    } catch (error) {
      console.error(`Error fetching enumeration data:`, error);
      setIsLoading(false);
    }
  }

  //... Service call to get all notification List.
  const getEmailNotificationList = async () => {
    setIsLoading(true);

    try {
      const { fromDate, toDate } = dates;
      const reportData = await EmailNotificationService.getSearchNotifications(
        fromDate,
        toDate,
        emailStatus
      );

      const data = reportData.data;
      setNotificationList(data);
      setNotificationListCopy(data);

      searchResultCount.current = data.length || 0;

      setPagingInfo({
        ...pagingInfo,
        noOfPages: Math.ceil(data.length / pagingInfo.rowsPerPage),
      });

      setIsLoading(false);
    } catch (error) {
      console.log("Error fetching notifications", error);
      setIsLoading(false);
    }
  };

  //... Service call to delete saved file.
  const deleteMediaFromDB = async () => {
    try {
      setIsLoading(true);
      const { id } = selectedFileRef.current;
      const result =
        await EmailNotificationService.deleteEmailNotificationDetails(
          id,
          user.loginID
        );

      if (result) {
        setIsLoading(false);
        showAlert(
          true,
          "success",
          "Notification details deleted successfully."
        );
        getEmailNotificationList();
      }
    } catch (error) {
      setIsLoading(false);
      showAlert(true, "error", "Error while deleting Notification details.");
      console.error("Error while deleting Notification details:", error);
    }
  };

  //.. Utitlity to set in-post game list along with pagination info.
  const getReviewList = (page_number, page_size) => {
    let list = notificationList?.slice(
      (page_number - 1) * page_size,
      page_number * page_size
    );
    setCurrentPageList(list ?? []);
  };

  //..Utility to validate value
  const validateValue = useCallback((value) => {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }

    if (typeof value === "number") {
      return value > 0 ? value : 0;
    }

    return "--"; // Default return when the value doesn't meet the conditions
  }, []);

  //... Utility to get tables column.
  const getTableColumns = () => {
    return [
      "Recipients",
      "Additional Email-Ids",
      "From Name",
      "Subject Line",
      "Schedule Date",
      "Email Status",
      "",
    ];
  };

  //... Utility to get current states list...
  const getCurrentSearchFilters = () => ({
    dates,
    emailStatus,
  });

  //... Utility to compare two state objects
  const areStatesEqual = (state1, state2) => {
    return JSON.stringify(state1) === JSON.stringify(state2);
  };

  //... Utility to set preserved states data to current states.
  const setPreservedStates = (states) => {
    const { dates, emailStatus } = states;
    setDates({
      fromDate: dayjs(dates.fromDate),
      toDate: dayjs(dates.toDate),
    });
    setEmailStatus(emailStatus);
  };

  //... Utility to show alert.
  const showAlert = (error, type, message) => {
    setAlert(error);
    setAlertType(type);
    setAlertMessage(message);
  };

  //... Utility to Render table header.
  const renderTableHeader = () => {
    return getTableColumns().map((column, index) => (
      <StyledTableCell
        key={index}
        size="small"
        align="left"
        width={columnWidths[column] || "auto"}
        sx={{
          "& span": {
            whiteSpace: "nowrap", // Conditionally set whiteSpace
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
      >
        {sortableHeaders.includes(column) && (
          <TableSortLabel
            active={orderBy === column}
            classes={{
              root: classes.tablesortlabelroot,
              active: classes.tablesortactivelabel,
              icon: classes.tablesorticon,
            }}
            direction={orderBy === column ? order : "asc"}
            onClick={() => handleSort(column)}
          >
            {column}
          </TableSortLabel>
        )}
        {!sortableHeaders.includes(column) && <span>{column}</span>}
      </StyledTableCell>
    ));
  };

  return (
    <>
      <Loader isLoading={isLoading} />
      <AppBar position="static" className={classes.appbarBackground}>
        <Toolbar>
          <Typography variant="h6" className={`${classes.title} header-title`}>
            Manage Email-Notifications
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth={false}>
        <Grid container spacing={1} sx={{ my: 2 }}>
          {["fromDate", "toDate"].map((dateKey) => (
            <Grid item lg={2} md={3} sm={6} xs={6} key={dateKey}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={dateKey === "fromDate" ? "From Date" : "To Date"}
                  value={dates[dateKey]}
                  onChange={(newValue) => handleDateChange(dateKey, newValue)}
                />
              </LocalizationProvider>
            </Grid>
          ))}

          <Grid item lg={2} md={3} sm={6} xs={12}>
            <FormControl fullWidth>
              <InputLabel>Email Status</InputLabel>
              <Select
                value={emailStatus}
                onChange={handleEmailStatusChange}
                label="Email Status"
              >
                {emailStatusList &&
                  emailStatusList.length > 0 &&
                  emailStatusList?.map((option) => (
                    <MenuItem key={option.childID} value={option.childID}>
                      {option.childName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid
            item
            container
            alignItems="center"
            justify="flex-end"
            lg={2}
            md={9}
            sm={9}
            xs={12}
            className={classes.gridItem}
          >
            {" "}
            <Button
              size="small"
              onClick={() => getEmailNotificationList()}
              variant="contained"
              color="primary"
              className={classes.btn}
            >
              Get Notifications
            </Button>
          </Grid>
        </Grid>
        <Divider />
        <Grid container alignItems="center" spacing={2} sx={{ my: 2 }}>
          <Grid item xs={10}>
            {/* Pagination component on the left side */}
            <Paging
              pagingInfo={pagingInfo}
              handleRowPerPageChange={handleRowPerPageChange}
              onPageChange={onPageChange}
              className={classes.pagination}
            />
          </Grid>
          <Grid item xs={2} textAlign="right">
            {/* Stack items on the right side */}
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              {" "}
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => handleNotificationAction(0)}
              >
                <MarkEmailUnreadIcon
                  style={{
                    marginRight: "5px",
                    fontSize: 28,
                    cursor: "pointer",
                    color: "orange",
                  }}
                />
                <Hidden smDown>Send Notification</Hidden>
              </Button>
            </Box>
          </Grid>
        </Grid>

        <TableContainer
          component={Paper}
          className={classes.container}
          sx={{ my: 2 }}
        >
          <Table
            stickyHeader
            className={classes.table}
            aria-label="customized table"
          >
            {" "}
            <TableHead>
              <TableRow>{renderTableHeader()}</TableRow>
            </TableHead>
            <TableBody>
              {currentPageList && currentPageList.length > 0 ? (
                currentPageList.map((notification, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell>
                      {validateValue(notification.dL_OptionsText)}
                    </StyledTableCell>
                    <StyledTableCell>
                      <div
                        style={{
                          inlineSize: "250px", // Adjust the width as needed
                          overflowWrap: "break-word",
                        }}
                      >
                        {validateValue(notification.additional_To)}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell>
                      {validateValue(notification.fromName)}
                    </StyledTableCell>
                    <StyledTableCell>
                      {validateValue(notification.subject)}
                    </StyledTableCell>
                    <StyledTableCell>
                      {notification?.scheduledDate
                        ? dayjs(notification.scheduledDate).format(
                            "DD-MM-YYYY h:mm A"
                          )
                        : "--"}
                    </StyledTableCell>
                    <StyledTableCell>
                      {emailStatusList?.find(
                        (status) => status.childID === notification.emailStatus
                      )?.childName || "--"}
                    </StyledTableCell>

                    <StyledTableCell>
                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                      >
                        {![3].includes(notification.emailStatus) && (
                          <IconButton
                            onClick={() =>
                              handleNotificationAction(notification.id)
                            }
                          >
                            <TitleTooltip title="Edit" placement="bottom">
                              <EditIcon color="primary" />
                            </TitleTooltip>
                          </IconButton>
                        )}

                        <IconButton>
                          <TitleTooltip
                            title="View"
                            placement="bottom"
                            key={index}
                          >
                            <VisibilityIcon
                              onClick={() =>
                                handleNotificationAction(
                                  notification.id,
                                  "view"
                                )
                              }
                              color="primary"
                            />
                          </TitleTooltip>
                        </IconButton>

                        {![3].includes(notification.emailStatus) && (
                          <IconButton>
                            <TitleTooltip
                              title="Delete"
                              placement="bottom"
                              key={index}
                            >
                              <DeleteForeverIcon
                                onClick={() => handleDeleteMedia(notification)}
                                color="secondary"
                                hover="hand"
                              />
                            </TitleTooltip>
                          </IconButton>
                        )}
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={6} width="100%">
                    <Typography component="h6" style={{ padding: "20px" }}>
                      Record not found for respective search criteria.....
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {/* Document Delete Dialog Box */}
      <Dialog
        open={openDeleteDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleDeleteDialogClose}
        aria-describedby="alert-delete-dialog-slide-description"
        maxWidth="lg"
        PaperProps={{
          style: {
            width: "40%",
          },
        }}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            Do you wish to delete?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteDialogClose}
            variant="contained"
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDialogAgree}
            variant="contained"
            color="primary"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
        onClose={() => setAlert(false)}
      >
        <Alert
          onClose={() => setAlert(false)}
          severity={alertType}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ManageEmailNotification;
